import { isNotDefined } from '@sgme/fp';
import classnames from 'classnames';
import { FC, useCallback } from 'react';
import { useIntl } from 'react-intl';
import type { IWorkflowError } from 'utils/errors/workflowErrors';

export interface IErrorMessageProps {
  errorMessage: IWorkflowError;
}

const id = 'errors-panel-details-list-error-message';

export const ErrorMessage: FC<IErrorMessageProps> = ({ errorMessage }) => {
  const { formatMessage, formatNumber } = useIntl();
  const getFormattedValue = useCallback(
    (key: keyof IWorkflowError) => {
      const value = errorMessage[key];

      if (isNotDefined(value)) {
        return '-';
      }
      if (typeof value === 'string') {
        return value.replace(/:/gi, ': ');
      }

      if (typeof value === 'number') {
        return formatNumber(value);
      }

      return value;
    },
    [errorMessage, formatNumber],
  );

  // See https://stackoverflow.com/a/13850750
  // Otherwise, some error messages containing the long types force the message to overflow.
  const forceLineBreaks = {
    wordBreak: 'break-word' as 'break-word',
  };

  return (
    <table className={`${id}-component table table-sm`} data-e2e={id} id={id}>
      <tbody>
        {Object.keys(errorMessage).map((key: string) => (
          <tr
            key={key}
            className={classnames({
              'text-danger': key.toLowerCase() === 'originmessage',
            })}
          >
            <td className="w-25 " data-e2e={`error-details-${key.toLowerCase()}-key`}>
              {formatMessage({ id: `errorDetails.${key}` as any, defaultMessage: key })}
            </td>
            <td className="w-75" style={forceLineBreaks} data-e2e={`error-details-${key.toLowerCase()}-value`}>
              {getFormattedValue(key as keyof IWorkflowError)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
